import { ApiResult, HttpClient } from "../http.js";
import {
  AssignmentTradeType,
} from "../types/assignments.js";
import {
  CurrentUserDto,
  EmployeeAddress,
  EmployeeSalary,
  EmployeeSkill,
  MyCompanyDto,
  UserDto,
} from "../types/users.js";

/**
 * Body of `PATCH /api/users/me`. The self profile screen edits display name +
 * phone number only; admin-managed employee fields are not accepted here. Email
 * is RsConnect-managed and rejected by the backend.
 */
export interface UpdateCurrentUserPayload {
  displayName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
}

/**
 * Body of `PATCH /api/users/{id}` (admin member edit). Carries the full editable
 * employee profile, applied with whole-object replace semantics.
 */
export interface UpdateUserPayload {
  displayName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  address?: string | null;
  profileImage?: string | null;
  structuredAddress?: EmployeeAddress | null;
  salary?: EmployeeSalary | null;
  primaryTrade?: AssignmentTradeType | null;
  /** The target team's `teams.id`. The only way to set a team. */
  teamId?: string | null;
  skills?: EmployeeSkill[] | null;
  isActive?: boolean;
}

/**
 * `/api/users/*` — user profile reads + writes. `me` acts on the authenticated
 * user only (never a client-supplied id).
 */
export class UsersApi {
  constructor(private readonly http: HttpClient) {}

  /** GET /api/users/me — returns the current user + permissions in their active org. */
  me(): Promise<ApiResult<CurrentUserDto>> {
    return this.http.get<CurrentUserDto>("/api/users/me");
  }

  /** GET /api/users/me/companies — returns the caller's company memberships. */
  myCompanies(): Promise<ApiResult<MyCompanyDto[]>> {
    return this.http.get<MyCompanyDto[]>("/api/users/me/companies");
  }

  /** PATCH /api/users/me — updates the caller's editable profile fields. */
  updateMe(payload: UpdateCurrentUserPayload): Promise<ApiResult<CurrentUserDto>> {
    return this.http.patch<CurrentUserDto>("/api/users/me", { body: payload });
  }

  /** PATCH /api/users/{id} — updates a company member's editable identity fields. */
  update(id: string, payload: UpdateUserPayload): Promise<ApiResult<UserDto>> {
    return this.http.patch<UserDto>(`/api/users/${encodeURIComponent(id)}`, { body: payload });
  }

  /** GET /api/users/{id} — returns a single user. */
  get(id: string): Promise<ApiResult<UserDto>> {
    return this.http.get<UserDto>(`/api/users/${encodeURIComponent(id)}`);
  }
}
